import { useState, useEffect, useRef } from 'react';

/**
 * useLocalStorage - Hook for persisting state to localStorage
 * Story 2.7: Timeline Zoom & Scrolling
 *
 * @param key - localStorage key
 * @param defaultValue - default value if key not found
 * @returns [value, setValue] tuple
 */
export function useLocalStorage<T>(key: string, defaultValue: T): [T, (value: T) => void] {
    const [value, setValue] = useState<T>(() => {
        try {
            const stored = localStorage.getItem(key);
            return stored ? (JSON.parse(stored) as T) : defaultValue;
        } catch {
            return defaultValue;
        }
    });

    // Track whether we're past the initial mount so we only re-read on genuine key changes
    const isMounted = useRef(false);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return; // State initializer already read the value on mount
        }
        // Key changed (e.g. navigating between projects): load the saved value for the new key
        try {
            const stored = localStorage.getItem(key);
            setValue(stored ? (JSON.parse(stored) as T) : defaultValue);
        } catch {
            setValue(defaultValue);
        }
    // defaultValue is intentionally omitted: callers pass stable literals/enums
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    useEffect(() => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error('Failed to save to localStorage:', error);
        }
    }, [key, value]);

    return [value, setValue];
}
